import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Camera, Clock, ExternalLink, Loader2 } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useMapFilter } from '../contexts/MapFilterContext';

export const FeaturedStories = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { itemTypeFilter, searchTerm, privateTagMode, initialPrivateTag, theme: mapTheme } = useMapFilter();
  
  // Derive accent colors from MapFilter theme (club pages pass custom themes)
  const accentColor = mapTheme?.primaryColor || '#10b981'; // amber-500 for clubs, emerald for default
  const accentColorName = mapTheme?.primary || 'emerald';
  
  // Debug logging to track re-renders and filter changes
  console.log('🔄 FeaturedStories RENDER - Filter:', itemTypeFilter, 'Search:', searchTerm, 'PrivateTagMode:', privateTagMode, 'Timestamp:', new Date().toISOString());
  
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to randomly select 3 items from database
  const loadFeaturedItems = async () => {
    try {
      setLoading(true);
      console.log('📥 FeaturedStories loadFeaturedItems CALLED - Filter:', itemTypeFilter, 'searchTerm:', searchTerm, 'privateTagMode:', privateTagMode);
      
      let responseItems = [];
      
      // If in private tag mode AND showing traveling items, use the private tag endpoint
      // BUT if showing stationary (Location Guestbook), fetch ALL stationary items
      if (privateTagMode && initialPrivateTag && itemTypeFilter !== 'stationary') {
        console.log(`🏷️ FeaturedStories: Fetching traveling items by private tag: ${initialPrivateTag}`);
        const response = await fetch(
          `${API_BASE}/api/items/by-private-tag/${encodeURIComponent(initialPrivateTag)}`
        );
        const data = await response.json();
        if (data.success) {
          responseItems = data.items || [];
          console.log(`✅ FeaturedStories: Loaded ${responseItems.length} items with privateTag "${initialPrivateTag}"`);
        }
      } else {
        // Use optimized stories endpoint for normal mode OR stationary on club pages
        if (privateTagMode && itemTypeFilter === 'stationary') {
          console.log(`🏠 FeaturedStories: Club page Location Guestbook - fetching ALL stationary items`);
        }
        // Skip the random featured endpoint when a search term is active
        // (we need the full text-searchable list) or on club pages.
        const useRandomEndpoint = !privateTagMode && !(searchTerm && searchTerm.trim().length > 0);
        if (useRandomEndpoint) {
          const featuredResp = await fetch(
            `${API_BASE}/api/featured-stories/random?count=3&item_type=${itemTypeFilter === 'all' ? '' : itemTypeFilter}`
          );
          const featuredData = await featuredResp.json();
          responseItems = featuredData.items || [];
        } else {
          const response = await fetch(
            `${API_BASE}/api/stories-feed?skip=0&limit=50&item_type=${itemTypeFilter === 'all' ? '' : itemTypeFilter}`
          );
          const data = await response.json();
          responseItems = data.items || [];
        }
      }
      
      if (responseItems && Array.isArray(responseItems) && responseItems.length > 0) {
        // Filter items with valid stories and names
        let validItems = responseItems.filter(item => 
          item.stories && 
          Array.isArray(item.stories) && 
          item.stories.length > 0 &&
          item.name && 
          item.name.trim().length > 0
        );
        
        // Apply item type filter (same as InteractiveMap)
        // In privateTagMode with traveling, we already have filtered items, just apply itemType filter
        // In privateTagMode with stationary, we're showing ALL stationary items
        console.log('Before filter - validItems count:', validItems.length);
        if (itemTypeFilter !== 'all') {
          validItems = validItems.filter(item => item.itemType === itemTypeFilter);
          console.log(`After filtering for ${itemTypeFilter} - validItems count:`, validItems.length);
        }
        
        // Apply search term filter if present (skip for privateTagMode traveling since we already filtered)
        if (searchTerm && searchTerm.trim().length > 0 && !(privateTagMode && itemTypeFilter !== 'stationary')) {
          const searchLower = searchTerm.toLowerCase();
          validItems = validItems.filter(item => {
            return (
              item.name?.toLowerCase().includes(searchLower) ||
              item.currentLocation?.toLowerCase().includes(searchLower) ||
              item.category?.toLowerCase().includes(searchLower) ||
              item.privateTag?.toLowerCase().includes(searchLower) ||
              item.stories?.some(story => 
                story.location?.toLowerCase().includes(searchLower) ||
                story.story?.toLowerCase().includes(searchLower)
              )
            );
          });
        }
        
        if (validItems.length > 0) {
          // Deduplicate by fingerprint of the LATEST story (image+text+location)
          // so that Batch-Story-Processing items which share the exact same story
          // don't collapse into visually identical cards on the homepage.
          const seenUids = new Set();
          const seenFingerprints = new Set();
          const normalize = (v) => (v || '').toString().trim().toLowerCase();
          const uniqueItems = [];
          for (const item of validItems) {
            if (!item.uid || seenUids.has(item.uid)) continue;
            const latestStory = item.stories[item.stories.length - 1];
            if (!latestStory) continue;
            const fingerprint = [
              normalize(latestStory.image),
              normalize(latestStory.story),
              normalize(latestStory.location),
            ].join('|');
            if (fingerprint !== '||' && seenFingerprints.has(fingerprint)) continue;
            seenUids.add(item.uid);
            seenFingerprints.add(fingerprint);
            uniqueItems.push(item);
          }
          
          console.log(`📖 Filtered ${validItems.length} items down to ${uniqueItems.length} unique stories`);
          
          // Randomly shuffle and take up to 3 items
          const shuffled = [...uniqueItems].sort(() => 0.5 - Math.random());
          const selectedItems = shuffled.slice(0, Math.min(3, shuffled.length));
          setFeaturedItems(selectedItems);
        } else {
          setFeaturedItems([]);
        }
      } else {
        setFeaturedItems([]);
      }
    } catch (error) {
      console.error('Error loading featured items:', error);
      setFeaturedItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Load featured items on component mount and when filters change
  useEffect(() => {
    console.log('🔥 FeaturedStories useEffect TRIGGERED - Filter:', itemTypeFilter, 'Search:', searchTerm, 'PrivateTagMode:', privateTagMode, 'Timestamp:', new Date().toISOString());
    loadFeaturedItems();
  }, [itemTypeFilter, searchTerm, privateTagMode, initialPrivateTag]);

  const handleViewJourney = (item) => {
    navigate(`/item/${item.uid}`);
  };

  const handleExploreAllStories = () => {
    navigate('/all-stories');
  };

  if (loading) {
    return (
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Amazing Stories
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Discover incredible stories from items traveling around the world
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
            <span className="ml-2 text-slate-600">Loading amazing stories...</span>
          </div>
        </div>
      </section>
    );
  }

  if (featuredItems.length === 0) {
    return (
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Amazing Stories
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Discover incredible stories from items traveling around the world
            </p>
          </div>
          <div className="text-center py-12">
            <p className="text-slate-600 mb-6">No stories to display yet. Be the first to create an amazing story!</p>
            <Button 
              onClick={() => navigate('/create-item')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Create a QR code to attach to an item
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-12 bg-slate-50"
      style={{
        fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit'
      }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 
            className={`text-4xl md:text-5xl font-bold mb-4 ${theme.cards.heading}`}
            style={{ fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit' }}
          >
            Amazing Stories
          </h2>
          {/* Debug: Show current filter */}
          <div className="mb-2">
            <Badge className="bg-blue-500 text-white">
              Showing: {itemTypeFilter === 'traveling' ? 'Traveling Items' : itemTypeFilter === 'stationary' ? 'Stationary Locations' : 'All Items'}
              {searchTerm && ` - Search: "${searchTerm}"`}
            </Badge>
          </div>
          <p className={`text-xl max-w-2xl mx-auto ${theme.cards.text}`}>
            Discover incredible stories from items {itemTypeFilter === 'traveling' ? 'traveling around the world' : itemTypeFilter === 'stationary' ? 'at locations worldwide' : 'from around the world'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {featuredItems.map((item) => {
            // Get the latest story (most recent)
            const latestStory = item.stories && item.stories.length > 0 
              ? item.stories[item.stories.length - 1] 
              : null;

            if (!latestStory) return null;

            // Walk stories newest → oldest and pick the first one with a photo.
            // This keeps the featured card feeling dynamic instead of showing
            // the same static initialImageUrl / locationPhotoUrl every time.
            const mostRecentStoryImage = (() => {
              if (!item.stories) return null;
              for (let i = item.stories.length - 1; i >= 0; i--) {
                const s = item.stories[i];
                const img = s?.image || s?.imageUrl;
                if (img) return img;
              }
              return null;
            })();

            return (
              <Card 
                key={item.uid} 
                className={`group hover:shadow-2xl transition-all duration-500 overflow-hidden bg-white border-2 border-slate-200 hover:border-emerald-300`}
                style={{
                  fontFamily: theme.id === 'vintage' ? 'Georgia, serif' : 'inherit'
                }}
              >
                <div className="relative">
                  {/* Prefer the most recent STORY image so the card feels
                      dynamic on every new upload. Fall back to the item's
                      initial / location photo only when no story has an image. */}
                  {(() => {
                    const imageUrl = mostRecentStoryImage
                      || (item.itemType === 'stationary'
                        ? (item.locationPhotoUrl || item.initialImageUrl)
                        : (item.initialImageUrl || item.locationPhotoUrl));
                    
                    return imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={`${item.name} journey`}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          // Fallback to gradient if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  <div 
                    className={`w-full h-48 bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform duration-500 ${
                      (() => {
                        const hasImage = mostRecentStoryImage
                          || (item.itemType === 'stationary'
                            ? (item.locationPhotoUrl || item.initialImageUrl)
                            : (item.initialImageUrl || item.locationPhotoUrl));
                        return hasImage ? 'hidden' : 'flex';
                      })()
                    }`}
                  >
                    <div className="text-center text-white">
                      <div className="text-4xl mb-2">📍</div>
                      <div className="text-sm font-medium">{item.name}</div>
                    </div>
                  </div>
                  
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-gray-900 hover:bg-white">
                      {item.totalScans || item.stories?.length || 0} scans
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    {item.itemType === 'stationary' ? (
                      <Badge variant="secondary" style={{ backgroundColor: accentColor }} className="text-white">
                        {item.stories?.length || 0} {item.stories?.length === 1 ? 'entry' : 'entries'}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" style={{ backgroundColor: accentColor }} className="text-white">
                        {item.distance || '0 miles'}
                      </Badge>
                    )}
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{item.name}</h3>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {(() => {
                          const loc = item.currentLocation || '';
                          if (/^-?\d+\.?\d*,\s*-?\d+\.?\d*$/.test(loc.trim())) {
                            return 'Location recorded';
                          }
                          return loc || 'Unknown location';
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: `linear-gradient(135deg, ${accentColor}, ${mapTheme?.secondaryColor || '#3b82f6'})` }}>
                        <span className="text-white text-sm font-bold">
                          {(latestStory.contributorName || latestStory.userName || latestStory.user || '?').charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{latestStory.contributorName || latestStory.userName || latestStory.user || 'Anonymous'}</div>
                        <div className="text-xs text-gray-600 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {(() => {
                            const raw = latestStory.timestamp || latestStory.date;
                            if (!raw) return 'Recently';
                            const d = new Date(raw);
                            if (isNaN(d.getTime())) return 'Recently';
                            // e.g. "Aug 22, 2026"
                            return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                          })()}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">
                      {latestStory.story || latestStory.content || (latestStory.location ? `Found in ${latestStory.location}` : 'A new adventure begins...')}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <Camera className="w-4 h-4 mr-1" />
                        {item.stories?.length || 0} stories
                      </span>
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {item.countries || 0} countries
                      </span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className={`text-${accentColorName}-600 hover:text-${accentColorName}-700 hover:bg-${accentColorName}-50`}
                      onClick={() => handleViewJourney(item)}
                    >
                      View Stories
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button 
            size="lg" 
            className="text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            style={{ backgroundColor: accentColor }}
            onClick={handleExploreAllStories}
          >
            Explore All Stories
          </Button>
        </div>
      </div>
    </section>
  );
};